import { zodResolver } from '@hookform/resolvers/zod'
import {
  BookOpen,
  Clock,
  Globe,
  Hash,
  Layers,
  LayoutTemplate,
  MessageSquare,
  Minus,
  Plus,
  Sparkles,
  TextCursorInput,
  Wand2,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/feedback/FormErrorSummary'
import { JobProgressPanel } from '@/components/feedback/JobProgressPanel'
import { BackToFactoryLink } from '@/features/factory/components/BackToFactoryLink'
import { GenerationTimeHint } from '@/features/factory/components/GenerationTimeHint'
import { SuggestPromptButton } from '@/features/factory/components/SuggestPromptButton'
import { Button } from '@/components/ui/Button'
import { IconButton } from '@/components/ui/IconButton'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { useStartEtymology, useSuggestPrompt, type EtymologyInput } from '@/api/endpoints/factories'
import { useThemes, type Theme } from '@/api/endpoints/themes'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import { cn } from '@/lib/cn'
import { LlmProviderPicker } from '../_shared/LlmProviderPicker'
import { NarratorPicker } from '../_shared/NarratorPicker'
import { PresetBar } from '../_shared/PresetBar'
import { CinemaBackground } from '../ViralShortsForm/CinemaBackground'
import styles from '../ViralShortsForm/ViralShortsForm.module.scss'

const FIELD_LABELS: Record<string, string> = {
  theme: 'Tema',
  count: 'Quantidade',
  language: 'Idioma',
  custom_prompt: 'Prompt',
  num_words: 'Palavras',
  seconds_per_word: 'Tempo por palavra',
  presentation_mode: 'Modo',
  tone: 'Tom',
  tts_voice_pt: 'Narrador (PT)',
  tts_voice_en: 'Narrador (EN)',
}

type PresentationModeKey = 'definition' | 'quiz'
const PRESENTATION_MODES: ReadonlyArray<SegmentedOption<PresentationModeKey>> = [
  { value: 'definition', label: 'Definição direta' },
  { value: 'quiz', label: 'Quiz com countdown' },
]

const LANGUAGES: ReadonlyArray<SegmentedOption<'pt-br' | 'en' | 'both'>> = [
  { value: 'pt-br', label: 'PT-BR' },
  { value: 'en', label: 'EN' },
  { value: 'both', label: 'Ambos' },
]

type SecondsKey = '3' | '5' | '7' | '10'
const SECONDS_OPTIONS: ReadonlyArray<SegmentedOption<SecondsKey>> = [
  { value: '3', label: '3s' },
  { value: '5', label: '5s' },
  { value: '7', label: '7s' },
  { value: '10', label: '10s' },
]

type ToneKey = 'formal' | 'curious' | 'sarcastic'
const TONES: ReadonlyArray<SegmentedOption<ToneKey>> = [
  { value: 'formal', label: 'Formal' },
  { value: 'curious', label: 'Curioso' },
  { value: 'sarcastic', label: 'Debochado' },
]

const META_THEME_OPTIONS = [{ value: 'custom', label: 'Prompt customizado' }] as const

const schema = z
  .object({
    theme: z.string().min(1, 'Escolha um tema ou prompt'),
    count: z.number().int().min(1).max(10),
    language: z.enum(['pt-br', 'en', 'both']),
    num_words: z.number().int().min(2).max(10),
    seconds_per_word: z.number().int().min(3).max(10),
    presentation_mode: z.enum(['definition', 'quiz']),
    custom_prompt: z.string().trim().optional(),
    tone: z.enum(['formal', 'curious', 'sarcastic']).optional(),
    tts_voice_pt: z.string().optional(),
    tts_voice_en: z.string().optional(),
    llm_provider: z.enum(['auto', 'cloud', 'local']).optional(),
  })
  // Empty custom_prompt is valid — backend auto-generates via suggest_prompt.
  .refine(
    (v) => {
      if (v.theme !== 'custom') return true
      const len = v.custom_prompt?.length ?? 0
      return len === 0 || len >= 10
    },
    {
      path: ['custom_prompt'],
      message: 'Descreva com pelo menos 10 caracteres, ou deixe em branco para gerar um automaticamente.',
    },
  )

type FormValues = z.infer<typeof schema>

const FORM_DEFAULTS: FormValues = {
  theme: 'etymology-slang_brasileiro',
  count: 1,
  language: 'pt-br',
  num_words: 5,
  seconds_per_word: 5,
  presentation_mode: 'definition',
  custom_prompt: '',
}

function formToPayload(values: FormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    theme: values.theme,
    language: values.language,
    num_words: values.num_words,
    seconds_per_word: values.seconds_per_word,
    presentation_mode: values.presentation_mode,
    custom_prompt: values.theme === 'custom' && values.custom_prompt ? values.custom_prompt : '',
  }
  if (values.tone) payload.tone = values.tone
  if (values.tts_voice_pt) payload.tts_voice_pt = values.tts_voice_pt
  if (values.tts_voice_en) payload.tts_voice_en = values.tts_voice_en
  return payload
}

function payloadToForm(params: Record<string, unknown>): FormValues {
  const v: FormValues = {
    ...FORM_DEFAULTS,
    theme: (params.theme as string | undefined) ?? FORM_DEFAULTS.theme,
    language:
      ((params.language as FormValues['language'] | undefined) ?? null) ?? FORM_DEFAULTS.language,
    num_words: (params.num_words as number | undefined) ?? FORM_DEFAULTS.num_words,
    seconds_per_word:
      (params.seconds_per_word as number | undefined) ?? FORM_DEFAULTS.seconds_per_word,
    presentation_mode:
      (params.presentation_mode as PresentationModeKey | undefined) ??
      FORM_DEFAULTS.presentation_mode,
    custom_prompt: (params.custom_prompt as string | undefined) ?? '',
  }
  if (params.tone) v.tone = params.tone as FormValues['tone']
  if (params.tts_voice_pt) v.tts_voice_pt = params.tts_voice_pt as string
  if (params.tts_voice_en) v.tts_voice_en = params.tts_voice_en as string
  return v
}

export function EtymologyForm() {
  const { active } = useActiveChannelFromUrl()
  const channel = active?.slug ?? ''

  const { data: allThemes = [], isPending: themesPending } = useThemes()
  // Only etymology-* themes apply here. The themes endpoint returns every
  // JSON in config/themes/, so filter client-side by slug prefix.
  const themes = allThemes.filter((t: Theme) => t.slug.startsWith('etymology-'))

  const startMutation = useStartEtymology()
  const suggestPrompt = useSuggestPrompt()

  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      theme: themes[0]?.slug ?? 'etymology-slang_brasileiro',
      count: 1,
      language: 'pt-br',
      num_words: 5,
      seconds_per_word: 5,
      presentation_mode: 'definition',
      custom_prompt: '',
      llm_provider: 'auto',
    },
  })

  const theme = form.watch('theme')
  const count = form.watch('count')
  const language = form.watch('language')
  const numWords = form.watch('num_words')
  const secondsPerWord = form.watch('seconds_per_word')
  const presentationMode = form.watch('presentation_mode')
  const tone = form.watch('tone')
  const voicePt = form.watch('tts_voice_pt')
  const voiceEn = form.watch('tts_voice_en')
  const llmProvider = form.watch('llm_provider')

  const onSubmit = form.handleSubmit((values) => {
    const payload: EtymologyInput = {
      theme: values.theme,
      channel: channel || null,
      count: values.count,
      language: values.language,
      num_words: values.num_words,
      seconds_per_word: values.seconds_per_word,
      presentation_mode: values.presentation_mode,
      force: false,
      custom_prompt: values.custom_prompt ?? '',
      tone: values.tone ?? null,
      tts_voice_pt: values.tts_voice_pt || null,
      tts_voice_en: values.tts_voice_en || null,
      llm_provider: values.llm_provider ?? 'auto',
    }
    startMutation.mutate(payload, {
      onSuccess: (data) => {
        setActiveJobId(data.job_id)
      },
    })
  })

  if (activeJobId) {
    return (
      <div className={styles.panelShell}>
        <JobProgressPanel jobId={activeJobId} />
        <div className={styles.panelActions}>
          <Button
            variant="secondary"
            onClick={() => {
              setActiveJobId(null)
              form.reset()
            }}
          >
            Gerar outro
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.shell}>
      <CinemaBackground />
      <div className={styles.content}>
        <header className={styles.header}>
          <div className={styles.kicker}>
            <BookOpen aria-hidden /> Etimologia · de onde vêm as palavras
          </div>
          <h1 className={styles.title}>Que palavras vamos abrir hoje?</h1>
          <p className={styles.subtitle}>
            Escolha um tema; o Prism gera N palavras com três candidatos de origem, countdown e revelação ancorada em ano e fonte.
          </p>
        </header>

        <BackToFactoryLink />

        <form className={styles.glass} onSubmit={onSubmit} noValidate>
          {startMutation.isError ? (
            <FormErrorSummary error={startMutation.error} fieldLabels={FIELD_LABELS} />
          ) : null}
          {channel ? (
            <PresetBar<FormValues>
              factory="etymology"
              channel={channel}
              formValues={form.watch()}
              formToPayload={formToPayload}
              payloadToForm={payloadToForm}
              onApplyPreset={(values) => {
                form.reset(values)
              }}
            />
          ) : null}

          <div className={styles.rows}>
            <Row icon={<Hash />} label="Tema" htmlFor="etymology-theme">
              <SelectRoot
                value={theme}
                onValueChange={(v) => {
                  form.setValue('theme', v, { shouldValidate: true })
                }}
              >
                <SelectTrigger id="etymology-theme" aria-label="Tema">
                  <SelectValue placeholder={themesPending ? 'Carregando...' : 'Escolha um tema'} />
                </SelectTrigger>
                <SelectContent>
                  {META_THEME_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                  {themes.map((t: Theme) => (
                    <SelectItem key={t.slug} value={t.slug}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
              {form.formState.errors.theme ? (
                <span className={styles.errorText}>{form.formState.errors.theme.message}</span>
              ) : null}
            </Row>

            {theme === 'custom' ? (
              <Row icon={<Wand2 />} label="Prompt" htmlFor="etymology-prompt">
                <textarea
                  id="etymology-prompt"
                  className={cn(
                    styles.textarea,
                    form.formState.errors.custom_prompt && styles.textareaError,
                  )}
                  placeholder="Ex: 5 palavras corporativas com origem em manuais militares romanos"
                  rows={3}
                  {...form.register('custom_prompt')}
                />
                <SuggestPromptButton
                  factory="etymology"
                  language={language}
                  channel={channel}
                  pending={suggestPrompt.isPending}
                  error={suggestPrompt.error}
                  onClick={() => {
                    suggestPrompt.mutate(
                      { factory: 'etymology', language, channel: channel || null },
                      {
                        onSuccess: (prompt) => {
                          form.setValue('custom_prompt', prompt, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        },
                      },
                    )
                  }}
                />
                {form.formState.errors.custom_prompt ? (
                  <span className={styles.errorText}>
                    {form.formState.errors.custom_prompt.message}
                  </span>
                ) : null}
              </Row>
            ) : null}

            <Row icon={<TextCursorInput />} label="Palavras">
              <div className={styles.stepper}>
                <IconButton
                  aria-label="Diminuir"
                  variant="ghost"
                  size="sm"
                  disabled={numWords <= 2}
                  onClick={() => {
                    form.setValue('num_words', Math.max(2, numWords - 1))
                  }}
                >
                  <Minus />
                </IconButton>
                <span className={styles.stepperValue}>{numWords}</span>
                <IconButton
                  aria-label="Aumentar"
                  variant="ghost"
                  size="sm"
                  disabled={numWords >= 10}
                  onClick={() => {
                    form.setValue('num_words', Math.min(10, numWords + 1))
                  }}
                >
                  <Plus />
                </IconButton>
              </div>
            </Row>

            {presentationMode === 'quiz' ? (
              <Row icon={<Clock />} label="Tempo por palavra">
                <Segmented<SecondsKey>
                  options={SECONDS_OPTIONS}
                  value={String(secondsPerWord) as SecondsKey}
                  onValueChange={(v) => {
                    form.setValue('seconds_per_word', Number(v))
                  }}
                  aria-label="Tempo de countdown por palavra"
                />
              </Row>
            ) : null}

            <Row icon={<Layers />} label="Quantidade">
              <div className={styles.stepper}>
                <IconButton
                  aria-label="Diminuir"
                  variant="ghost"
                  size="sm"
                  disabled={count <= 1}
                  onClick={() => {
                    form.setValue('count', Math.max(1, count - 1))
                  }}
                >
                  <Minus />
                </IconButton>
                <span className={styles.stepperValue}>{count}</span>
                <IconButton
                  aria-label="Aumentar"
                  variant="ghost"
                  size="sm"
                  disabled={count >= 10}
                  onClick={() => {
                    form.setValue('count', Math.min(10, count + 1))
                  }}
                >
                  <Plus />
                </IconButton>
              </div>
            </Row>

            {advancedOpen ? (
              <div className={styles.advanced}>
                <AdvSection icon={<LayoutTemplate />} label="Modo">
                  <Segmented<PresentationModeKey>
                    options={PRESENTATION_MODES}
                    value={presentationMode}
                    onValueChange={(v) => {
                      form.setValue('presentation_mode', v)
                    }}
                    aria-label="Modo de apresentação"
                  />
                </AdvSection>

                <AdvSection icon={<Globe />} label="Idioma">
                  <Segmented<'pt-br' | 'en' | 'both'>
                    options={LANGUAGES}
                    value={language}
                    onValueChange={(v) => {
                      form.setValue('language', v)
                    }}
                    aria-label="Idioma"
                  />
                </AdvSection>

                <AdvSection icon={<MessageSquare />} label="Tom">
                  <Segmented<ToneKey>
                    options={TONES}
                    value={tone ?? ('' as ToneKey)}
                    onValueChange={(v) => {
                      form.setValue('tone', v)
                    }}
                    aria-label="Tom"
                  />
                </AdvSection>

                <NarratorPicker
                  language={language}
                  voicePt={voicePt ?? ''}
                  voiceEn={voiceEn ?? ''}
                  onVoicePtChange={(v) => {
                    form.setValue('tts_voice_pt', v)
                  }}
                  onVoiceEnChange={(v) => {
                    form.setValue('tts_voice_en', v)
                  }}
                />

                <LlmProviderPicker
                  value={llmProvider ?? 'auto'}
                  onChange={(v) => {
                    form.setValue('llm_provider', v)
                  }}
                />
              </div>
            ) : null}
          </div>

          <footer className={styles.cta}>
            <button
              type="button"
              className={cn(styles.advToggle, advancedOpen && styles.advToggleOpen)}
              onClick={() => {
                setAdvancedOpen((o) => !o)
              }}
            >
              Ajuste fino
            </button>
            <div className={styles.ctaSpacer} />
            <GenerationTimeHint videoKind="etymology" />
            <button
              type="submit"
              className={styles.generateBtn}
              disabled={!channel || startMutation.isPending}
            >
              <Sparkles aria-hidden />
              {startMutation.isPending ? 'Iniciando...' : 'Gerar'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  )
}

interface RowProps {
  icon: ReactNode
  label: ReactNode
  htmlFor?: string
  children: ReactNode
}

function Row({ icon, label, htmlFor, children }: RowProps) {
  return (
    <div className={styles.row}>
      {htmlFor ? (
        <label className={styles.rowLabel} htmlFor={htmlFor}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </label>
      ) : (
        <div className={styles.rowLabel}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </div>
      )}
      <div className={styles.rowControl}>{children}</div>
    </div>
  )
}

interface AdvSectionProps {
  icon: ReactNode
  label: ReactNode
  children: ReactNode
}

function AdvSection({ icon, label, children }: AdvSectionProps) {
  return (
    <section className={styles.advSection}>
      <div className={styles.advSectionLabel}>
        <span className={styles.rowIcon} aria-hidden>
          {icon}
        </span>
        {label}
      </div>
      {children}
    </section>
  )
}

